
import { Component, Ref, Vue } from 'vue-property-decorator'
import ManageNoticeEdit from '@/components/templates/manage/ManageNoticeEdit.vue'
import { NotificationsApi } from '@/api/methods/NotificationsApi'
import { NotificationsApi as ManageNotificationsApi } from '@/api/methods/manage/NotificationsApi'
import { ApiConflictAppError } from '@/models/AppErrorClasses'
import NoticeUtils from '@/utils/NoticeUtils'

/**
 * お知らせメッセージ編集画面
 */
@Component({
  components: {
    ManageNoticeEdit,
  },
})
export default class AdminNoticeEdit extends Vue {
  /** テンプレート要素 */
  @Ref()
  manageNoticeEdit!: ManageNoticeEdit

  /** 初期処理完了フラグ */
  private isLoaded = false

  /** 現在日時 */
  private nowDate = new Date()

  /** 選択可能日付の最小値 */
  private minDate = this.nowDate

  /** 編集前タイトル */
  private defaultTitle = ''

  /** 編集前本文HTML */
  private defaultMessageHtml = ''

  /** 編集前開始日時(unixミリ秒) */
  private defaultStartTime = 0

  /** 編集前終了日時(unixミリ秒) */
  private defaultEndTime = 0

  /** 最終更新時刻(unix秒) */
  private lastUpdatedAt = 0

  /**
   * お知らせID
   *
   * @returns お知らせID
   */
  private get messageId(): number {
    return Number(this.$route.params.id)
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // お知らせ情報取得
    const notificationDetail = await NotificationsApi.notification(this.messageId)

    // 現在のお知らせ情報を設定
    this.defaultTitle = notificationDetail.title
    this.defaultMessageHtml = notificationDetail.message
    this.defaultStartTime = notificationDetail.startedAt * 1000
    this.defaultEndTime = notificationDetail.endedAt * 1000
    this.lastUpdatedAt = notificationDetail.updatedAt

    // 選択可能日付を計算し設定する
    this.minDate = this.calcMinDate(notificationDetail.startedAt)

    this.isLoaded = true

    this.$loading.complete()
  }

  /**
   * 選択可能日付の最小値を計算する
   *
   * @param currentStartUnixSec 現在の開始日時(unix秒)
   * @returns 選択可能日付
   */
  private calcMinDate(currentStartUnixSec: number): Date {
    const currentStartDate = new Date(currentStartUnixSec * 1000)
    return NoticeUtils.calcMinDate(this.nowDate, currentStartDate)
  }

  /**
   * キャンセルボタン押下時の処理
   */
  private eventCancel(): void {
    // 詳細画面に遷移
    this.transitionPrevPage()
  }

  /**
   * 保存ボタン押下時の処理
   */
  private async eventSubmit(): Promise<void> {
    this.$loading.start()

    // 入力値チェック
    if (!this.checkInputValues()) {
      this.$loading.complete()
      return
    }

    try {
      // 更新処理の実行
      await ManageNotificationsApi.updateNotification(
        this.messageId,
        this.manageNoticeEdit.title,
        this.manageNoticeEdit.messageHtml,
        this.manageNoticeEdit.dateTimeRangePicker.startTime / 1000,
        this.manageNoticeEdit.dateTimeRangePicker.endTime / 1000,
        this.lastUpdatedAt
      )
    } catch (error) {
      if (error instanceof ApiConflictAppError) {
        alert('他のユーザによって更新されています。')
        this.$loading.complete()

        // 詳細画面に遷移
        this.transitionPrevPage()

        return
      }

      // 409エラー以外の場合は例外を投げ直し、共通処理に渡す
      this.$loading.complete()
      throw error
    }

    this.$loading.complete()

    this.transitionPrevPage()
  }

  /**
   * 遷移元ページへ移動
   */
  private transitionPrevPage(): void {
    // 遷移元ページ情報の復元
    const prevPage = this.$prevPage.restorePage()

    if (!prevPage) {
      this.$router.push(`/admin/notice/${this.messageId}`)
      return
    }

    this.$router.push({
      path: prevPage.path,
      query: prevPage.query,
    })
  }

  /**
   * 入力値チェック
   *
   * @returns 入力値が正常か否か
   */
  private checkInputValues(): boolean {
    // 入力値チェック
    const errorMessage = NoticeUtils.checkNotice(this.manageNoticeEdit.title, this.manageNoticeEdit.messageHtml)
    if (errorMessage) {
      alert(errorMessage)
      return false
    }

    return true
  }
}
